import axios from 'axios';

/**
 * Centralized API Client with Standardized Response Handling
 * 
 * This client handles the new standardized API response format:
 * Success: { success: true, message: "...", data: {...}, meta: {...} }
 * Error: { success: false, message: "...", errors: {...} }
 */

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';

// Debounce session expired events to prevent multiple notifications
// when multiple requests fail simultaneously
let sessionExpiredEventPending = false;
const SESSION_EXPIRED_DEBOUNCE_MS = 100;

const dispatchSessionExpired = () => {
    if (!sessionExpiredEventPending) {
        sessionExpiredEventPending = true;
        // Use setTimeout to batch multiple 401/403 errors into a single event
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
            sessionExpiredEventPending = false;
        }, SESSION_EXPIRED_DEBOUNCE_MS);
    }
};

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // Important for cookie-based auth
    headers: {
        'Content-Type': 'application/json',
    },
});

/**
 * Response Interceptor
 * Handles the new standardized API response format
 */
apiClient.interceptors.response.use(
    (response) => {
        // Check if response follows new standardized format
        if (response.data && typeof response.data.success !== 'undefined') {
            // New standardized format
            if (!response.data.success) {
                // API returned success: false - treat as error
                const error = new Error(response.data.message || 'Request failed');
                error.response = response;
                error.apiError = true;
                error.errors = response.data.errors;
                return Promise.reject(error);
            }

            // Success response - return the whole response for access to data and meta
            return response.data;
        }

        // Legacy format or non-API response - return as is
        return response;
    },
    (error) => {
        // Handle HTTP errors (4xx, 5xx)
        if (error.response?.data) {
            const statusCode = error.response.status;

            // Dispatch event on 401/403 to notify auth context of session invalidation
            // 401 = no token provided, 403 = invalid/expired token
            // Skip for auth endpoints to avoid infinite loops during login/logout
            if ((statusCode === 401 || statusCode === 403) && !error.config?.url?.includes('/auth/')) {
                dispatchSessionExpired();
            }

            // Check if error response has standardized format
            if (typeof error.response.data.success !== 'undefined') {
                const apiError = new Error(error.response.data.message || 'Request failed');
                apiError.response = error.response;
                apiError.apiError = true;
                apiError.errors = error.response.data.errors;
                apiError.statusCode = statusCode;
                return Promise.reject(apiError);
            }

            // Legacy error format
            const message = error.response.data.message || error.response.data.error || 'Request failed';
            const apiError = new Error(message);
            apiError.response = error.response;
            apiError.statusCode = statusCode;
            return Promise.reject(apiError);
        }

        // Network error or other issue
        return Promise.reject(error);
    }
);

/**
 * Request Interceptor
 * Can be used to add auth tokens, logging, etc.
 */
apiClient.interceptors.request.use(
    (config) => {
        // Add any request modifications here
        // For example, adding auth tokens from localStorage
        // const token = localStorage.getItem('authToken');
        // if (token) {
        //   config.headers.Authorization = `Bearer ${token}`;
        // }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Helper function to extract validation errors
 * @param {Error} error - Error object from API
 * @returns {Object} - Field-level validation errors
 */
export const getValidationErrors = (error) => {
    if (error.apiError && error.errors?.details) {
        // Convert array of {field, message} to object {field: message}
        return error.errors.details.reduce((acc, { field, message }) => {
            acc[field] = message;
            return acc;
        }, {});
    }
    return {};
};

/**
 * Helper function to check if error is a validation error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isValidationError = (error) => {
    return error.apiError && error.statusCode === 400 && error.errors?.details;
};

/**
 * Helper function to check if error is an auth error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
    return error.statusCode === 401 || error.statusCode === 403;
};

export default apiClient;
